// src/app/modules/adminrh/Mesrequetes/MesrequetesJS/FeuillesTemps.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ChevronLeft, ChevronRight, Clock, Lock, CheckCircle, AlertCircle, RefreshCw, Eye, Plus, Trash2, X, Calendar, FileText } from "lucide-react";
import "../MesrequetesStyles/FeuillesTemps.css";
import "../MesrequetesStyles/MesOrdresShared.css";

import { API_BASE_URL } from "../../../../../api";
import {
    getWorkdaysConfigRequest,
    getCalendarSettingsRequest,
} from "../../../../redux/actions/workDaysActions";

import {
    fetchTimesheetsRequest,
    createTimesheetRequest,
    updateTimesheetRequest,
    fetchTimesheetDaysRequest,
    upsertTimesheetDayRequest,
    fetchTimesheetOvertimesRequest,
    upsertTimesheetOvertimeRequest,
    deleteTimesheetOvertimeRequest,
    submitOvertimeRequest,
} from "../../../../redux/actions/timesheetActions";

import { fetchMyProjetsRequest } from "../../../../redux/actions/projetActions";

// Helpers
const WEEKDAYS_FR = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const DAY_SHORT   = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const DAY_FULL    = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];

const toISO = (d) => {
    const x = new Date(d);
    x.setHours(0,0,0,0);
    return x.toISOString().split("T")[0];
};

// --- DONNÉES DE DÉMO (statiques, pour la présentation) ---
const DEMO_OVERTIMES = [
    {
        id: "demo-9001",
        isDemo: true,
        workDate: "2026-06-24",
        startTime: "18:00:00",
        endTime: "20:30:00",
        heures: 2.5,
        projetNom: "Projet 1",
        commentaire: "Réunion client tardive",
        statut: "PENDING_MANAGER",
    },
    {
        id: "demo-9002",
        isDemo: true,
        workDate: "2026-06-26",
        startTime: "19:00:00",
        endTime: "21:00:00",
        heures: 2,
        projetNom: "Projet 2",
        commentaire: "Astreinte technique",
        statut: "APPROVED",
    },
    {
        id: "demo-9003",
        isDemo: true,
        workDate: "2026-06-28",
        startTime: "17:30:00",
        endTime: "19:00:00",
        heures: 1.5,
        projetNom: "—",
        commentaire: "Clôture mensuelle",
        statut: "REJECTED",
        managerComment: "Merci de re-planifier cette activité en journée.",
    },
];

const dateFromISO = (value) => {
    if (!value) return null;
    const [year, month, day] = value.split("-").map(Number);
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day);
};

const isEditableTimesheetStatus = (statut) => {
    if (!statut) return true;
    return ["DRAFT", "REJECTED"].includes(statut.toUpperCase());
};

const getStatusDisplay = (statut) => {
    const s = statut ? statut.toUpperCase() : "NEW";
    switch (s) {
        case "PENDING_MANAGER":
            return { label: "En attente du manager", cls: "pending-manager" };
        case "PENDING_ADMIN":
            return { label: "En attente RH", cls: "pending-admin" };
        case "APPROVED":
            return { label: "Approuvée", cls: "approved" };
        case "REJECTED":
            return { label: "Rejetée", cls: "rejected" };
        case "DRAFT":
            return { label: "Brouillon", cls: "draft" };
        case "SUBMITTED":
            return { label: "Soumise", cls: "submitted" };
        default:
            return { label: "Non soumise", cls: "new" };
    }
};

const STATUS_STYLES = {
    approved: { background: "rgba(16,185,129,.12)", color: "#047857", border: "1px solid rgba(16,185,129,.28)" },
    rejected: { background: "rgba(239,68,68,.10)", color: "#b91c1c", border: "1px solid rgba(239,68,68,.24)" },
    submitted: { background: "rgba(59,130,246,.12)", color: "#1d4ed8", border: "1px solid rgba(59,130,246,.26)" },
    draft: { background: "rgba(148,163,184,.16)", color: "#475569", border: "1px solid rgba(148,163,184,.30)" },
    "pending-manager": { background: "rgba(245,158,11,.14)", color: "#92400e", border: "1px solid rgba(245,158,11,.30)" },
    "pending-admin": { background: "rgba(99,102,241,.12)", color: "#4338ca", border: "1px solid rgba(99,102,241,.28)" },
    new: { background: "rgba(42,92,169,.08)", color: "#1e3a8a", border: "1px solid rgba(42,92,169,.18)" },
};

const getStatusStyle = (cls) => STATUS_STYLES[cls] || STATUS_STYLES.new;

const mondayOf = (d) => {
    const x = new Date(d), day = x.getDay();
    x.setDate(x.getDate() - day + (day === 0 ? -6 : 1));
    x.setHours(0,0,0,0);
    return x;
};

const sundayOf = (d) => {
    const s = mondayOf(d), e = new Date(s);
    e.setDate(s.getDate() + 6);
    e.setHours(0,0,0,0);
    return e;
};

const weekDaysOf = (ref) => Array.from({length: 7}, (_, i) => {
    const d = new Date(mondayOf(ref));
    d.setDate(d.getDate() + i);
    d.setHours(0,0,0,0);
    return d;
});

const FeuillesTemps = () => {
    const dispatch = useDispatch();

    const [demoOvertimes, setDemoOvertimes] = useState(DEMO_OVERTIMES);

    const { workdaysConfig } = useSelector(s => s.workDays || {});
    const { timesheets = [], daysByTimesheetId = {}, overtimesByTimesheetId = {}, error = null } = useSelector(s => s.timesheet || {});
    const { myProjets = [] } = useSelector(s => s.projet || {});
    const user = useSelector(s => s.auth?.user);
    const employeeId = user?.id || user?.userId || Number(sessionStorage.getItem("uid"));

    const [activeTab, setActiveTab] = useState("normal"); // "normal", "overtime", "history"
    const [currentDate, setCurrentDate] = useState(new Date());

    // Saisie heures normales
    const [localHours, setLocalHours]   = useState({});
    const [localProjects, setLocalProjects] = useState({});
    const [submitting, setSubmitting]   = useState(false);

    // Saisie Overtime formulaire
    const [ovDate, setOvDate] = useState("");
    const [ovStart, setOvStart] = useState("18:00");
    const [ovEnd, setOvEnd] = useState("20:00");
    const [ovProj, setOvProj] = useState("");
    const [ovComment, setOvComment] = useState("");
    const [overtimeFeedback, setOvertimeFeedback] = useState("");
    const [overtimeSubmitting, setOvertimeSubmitting] = useState(false);

    // Pop-up détails historique
    const [selectedHistorySheet, setSelectedHistorySheet] = useState(null);
    const [historyDays, setHistoryDays] = useState([]);
    const [historyOvertimes, setHistoryOvertimes] = useState([]);
    const [viewingDetails, setViewingDetails] = useState(false);

    const today    = useMemo(() => new Date(), []);
    const todayISO = toISO(today);

    const periodStart = useMemo(() => toISO(mondayOf(currentDate)), [currentDate]);
    const periodEnd   = useMemo(() => toISO(sundayOf(currentDate)), [currentDate]);
    const overtimeReferenceDate = useMemo(() => dateFromISO(ovDate) || currentDate, [ovDate, currentDate]);
    const overtimePeriodStart = useMemo(() => toISO(mondayOf(overtimeReferenceDate)), [overtimeReferenceDate]);
    const overtimePeriodEnd   = useMemo(() => toISO(sundayOf(overtimeReferenceDate)), [overtimeReferenceDate]);

    // Initial load
    useEffect(() => {
        dispatch(getWorkdaysConfigRequest());
        dispatch(fetchTimesheetsRequest());
        dispatch(fetchMyProjetsRequest());
    }, [dispatch]);

    useEffect(() => {
        dispatch(getCalendarSettingsRequest(currentDate.getFullYear(), "MA"));
    }, [currentDate, dispatch]);

    // Reset local inputs when changing week
    useEffect(() => {
        setLocalHours({});
        setLocalProjects({});
    }, [periodStart]);

    // Calendar check
    const workDaysMap = useMemo(() => {
        const src = workdaysConfig?.workDays || {};
        const out = {};
        WEEKDAYS_FR.forEach(d => {
            const v = src[d] || {};
            const iw = (v.isWorking ?? v.working ?? true) === true;
            out[d] = { isWorking: iw, hours: iw ? Number(v.hours ?? 8) : 0 };
        });
        return out;
    }, [workdaysConfig]);

    const isNonWork = d => {
        let i = d.getDay();
        i = i === 0 ? 6 : i - 1;
        return workDaysMap[WEEKDAYS_FR[i]]?.isWorking === false;
    };

    const isFutureDay = d => {
        const x = new Date(d); x.setHours(0,0,0,0);
        const t = new Date(today); t.setHours(0,0,0,0);
        return x.getTime() > t.getTime();
    };

    const normalH = d => {
        let i = d.getDay();
        i = i === 0 ? 6 : i - 1;
        return Number(workDaysMap[WEEKDAYS_FR[i]]?.hours ?? 0);
    };

    // Current sheet
    const sheet = useMemo(() =>
            (timesheets || []).find(t => t.periodStart === periodStart && t.periodEnd === periodEnd) || null,
        [timesheets, periodStart, periodEnd]
    );

    const sheetId = sheet?.id || null;
    const statut  = sheet?.statut || null;

    const canEdit = useMemo(() => isEditableTimesheetStatus(statut), [statut]);

    const overtimeSheet = useMemo(() =>
            (timesheets || []).find(t => t.periodStart === overtimePeriodStart && t.periodEnd === overtimePeriodEnd) || null,
        [timesheets, overtimePeriodStart, overtimePeriodEnd]
    );

    const overtimeSheetId = overtimeSheet?.id || null;
    const overtimeStatut = overtimeSheet?.statut || null;
    const canEditOvertime = useMemo(() => isEditableTimesheetStatus(overtimeStatut), [overtimeStatut]);
    const isOvertimeClosed = Boolean(ovDate && overtimeSheet && !canEditOvertime);

    // Fetch details
    useEffect(() => {
        if (sheetId) {
            dispatch(fetchTimesheetDaysRequest(sheetId));
        }
    }, [sheetId, dispatch]);

    useEffect(() => {
        if (overtimeSheetId) {
            dispatch(fetchTimesheetOvertimesRequest(overtimeSheetId));
        }
    }, [overtimeSheetId, dispatch]);

    useEffect(() => {
        if (sheetId && sheetId !== overtimeSheetId) {
            dispatch(fetchTimesheetOvertimesRequest(sheetId));
        }
    }, [sheetId, overtimeSheetId, dispatch]);

    const backendRows = useMemo(() => (sheetId ? daysByTimesheetId[sheetId] || [] : []), [sheetId, daysByTimesheetId]);
    const overtimeRows = useMemo(() => {
        const rows = overtimeSheetId ? overtimesByTimesheetId[overtimeSheetId] || [] : [];
        const combined = [...rows, ...demoOvertimes];
        return ovDate ? combined.filter(o => o.workDate === ovDate) : combined;
    }, [overtimeSheetId, overtimesByTimesheetId, ovDate, demoOvertimes]);

    useEffect(() => {
        if (error) {
            setOvertimeFeedback(error);
        }
    }, [error]);

    useEffect(() => {
        if (backendRows.length > 0) {
            const hrs = {};
            const prjs = {};
            backendRows.forEach(r => {
                if (r?.workDate) {
                    hrs[r.workDate] = Number(r.heures || 0);
                    prjs[r.workDate] = r.projetId || "";
                }
            });
            setLocalHours(prev => ({ ...hrs, ...prev }));
            setLocalProjects(prev => ({ ...prjs, ...prev }));
        }
    }, [backendRows]);

    const handleHourChange = (dateKey, dateObj, val) => {
        if (!canEdit || isFutureDay(dateObj) || isNonWork(dateObj)) return;
        const max = normalH(dateObj) || 24;
        const num = Math.min(max, Math.max(0, Number(val) || 0));
        setLocalHours(prev => ({ ...prev, [dateKey]: num }));
    };

    const handleProjectChange = (dateKey, dateObj, projectId) => {
        if (!canEdit || isFutureDay(dateObj) || isNonWork(dateObj)) return;
        setLocalProjects(prev => ({ ...prev, [dateKey]: projectId }));
    };

    // Save logic
    const saveDaysAndOvertimes = (id) => {
        const days = Object.entries(localHours)
            .filter(([k, h]) => h > 0 && k <= todayISO)
            .sort(([a], [b]) => a.localeCompare(b));

        days.forEach(([dateKey, heures], index) => {
            const projId = localProjects[dateKey] ? Number(localProjects[dateKey]) : null;
            setTimeout(() => {
                dispatch(upsertTimesheetDayRequest({
                    timesheetId: id,
                    workDate: dateKey,
                    heures,
                    projetId: projId,
                    commentaire: ""
                }));
            }, index * 450);
        });
    };

    const handleSaisieSubmit = () => {
        if (!canEdit || submitting) return;
        const hasHours = Object.values(localHours).some(h => h > 0);
        if (!hasHours) return;

        setSubmitting(true);
        if (!sheetId) {
            dispatch(createTimesheetRequest({
                employeeId,
                periodStart,
                periodEnd,
                statut: "SUBMITTED"
            }));
        } else {
            dispatch(updateTimesheetRequest(sheetId, {
                employeeId: sheet?.employeeId || employeeId,
                periodStart,
                periodEnd,
                statut: "SUBMITTED"
            }));
            saveDaysAndOvertimes(sheetId);
        }

        setTimeout(() => {
            setSubmitting(false);
            dispatch(fetchTimesheetsRequest());
        }, 1500);
    };

    useEffect(() => {
        if (submitting && sheetId) {
            saveDaysAndOvertimes(sheetId);
        }
    }, [sheetId, submitting]);

    // Handle overtime submission
    const handleAddOvertime = (e) => {
        e.preventDefault();
        setOvertimeFeedback("");
        if (!ovDate || !ovStart || !ovEnd) return;
        if (isOvertimeClosed) {
            const msg = "Les heures supplementaires sont fermees pour cette date car la feuille de temps est déjà soumise ou approuvée.";
            setOvertimeFeedback(msg);
            alert(msg);
            return;
        }

        const dateObj = dateFromISO(ovDate);
        if (isFutureDay(dateObj)) {
            alert("Impossible de saisir des heures supplémentaires sur un jour futur.");
            return;
        }

        const payload = {
            workDate: ovDate,
            startTime: ovStart + ":00",
            endTime: ovEnd + ":00",
            projetId: ovProj ? Number(ovProj) : null,
            commentaire: ovComment
        };

        const runUpsert = (id) => {
            setOvertimeSubmitting(true);
            setOvertimeFeedback("Ajout de l'heure supplémentaire en cours...");
            dispatch(upsertTimesheetOvertimeRequest({
                ...payload,
                timesheetId: id
            }, {
                autoSubmit: true,
                onSuccess: () => {
                    setOvertimeSubmitting(false);
                    setOvertimeFeedback("Heure supplementaire envoyee en validation.");
                    setOvDate("");
                    setOvComment("");
                    setOvProj("");
                    dispatch(fetchTimesheetsRequest());
                    dispatch(fetchTimesheetOvertimesRequest(id));
                },
                onFail: (msg) => {
                    setOvertimeSubmitting(false);
                    setOvertimeFeedback(msg || "Erreur lors de l'ajout de l'heure supplementaire.");
                }
            }));
        };

        if (!overtimeSheetId) {
            setOvertimeSubmitting(true);
            setOvertimeFeedback("Creation de la periode puis ajout de l'heure supplementaire...");
            // Doit d'abord créer la timesheet
            dispatch(createTimesheetRequest({
                employeeId,
                periodStart: overtimePeriodStart,
                periodEnd: overtimePeriodEnd,
                statut: "DRAFT"
            }, {
                silent: true,
                onSuccess: (createdSheet) => {
                    if (!createdSheet?.id) {
                        setOvertimeSubmitting(false);
                        setOvertimeFeedback("La periode a ete creee mais son id est introuvable, impossible d'ajouter l'heure supplementaire.");
                        return;
                    }
                    runUpsert(createdSheet.id);
                },
                onFail: (msg) => {
                    setOvertimeSubmitting(false);
                    setOvertimeFeedback(msg || "Erreur creation de la periode.");
                }
            }));
        } else {
            runUpsert(overtimeSheetId);
        }
    };

    const handleDeleteOvertime = (ovId, isDemo) => {
        if (isDemo) {
            setDemoOvertimes(prev => prev.filter(o => o.id !== ovId));
            return;
        }
        if (!canEditOvertime) return;
        dispatch(deleteTimesheetOvertimeRequest(ovId, overtimeSheetId));
    };

    const handleSubmitOvertime = (ovId, isDemo) => {
        if (isDemo) {
            setDemoOvertimes(prev => prev.map(o => o.id === ovId ? { ...o, statut: "PENDING_MANAGER" } : o));
            return;
        }
        if (!canEditOvertime) return;
        dispatch(submitOvertimeRequest(ovId, overtimeSheetId));
    };

    // View history details modal
    const handleViewDetails = async (hSheet) => {
        setSelectedHistorySheet(hSheet);
        setViewingDetails(true);
        // Call endpoints directly or fetch to Redux
        const token = sessionStorage.getItem("token");
        const uid = sessionStorage.getItem("uid");
        const headers = { uid };
        try {
            const daysResp = await fetch(`${API_BASE_URL}/api/timesheets/${hSheet.id}/days`, {
                headers: { ...headers, Authorization: `Bearer ${token}` }
            });
            const daysData = await daysResp.json();
            setHistoryDays(daysData || []);

            const ovResp = await fetch(`${API_BASE_URL}/api/timesheets/${hSheet.id}/overtimes`, {
                headers: { ...headers, Authorization: `Bearer ${token}` }
            });
            const ovData = await ovResp.json();
            setHistoryOvertimes(ovData || []);
        } catch(err) {
            console.error("Error loading history details", err);
        }
    };

    // Day calculations
    const wDays = useMemo(() => weekDaysOf(currentDate).map((d, i) => {
        const dateKey = toISO(d);
        const hours = localHours[dateKey] !== undefined ? localHours[dateKey] : 0;
        const selectedProj = localProjects[dateKey] || "";

        return {
            dateObj: d,
            dateKey,
            short: DAY_SHORT[i],
            full: DAY_FULL[i],
            isOff: isNonWork(d),
            normalHours: normalH(d),
            isToday: dateKey === todayISO,
            isFutureDay: isFutureDay(d),
            hours,
            selectedProj
        };
    }), [currentDate, localHours, localProjects, todayISO, workDaysMap]);

    const totalSaisi = useMemo(() => wDays.reduce((s, d) => s + d.hours, 0), [wDays]);
    const totalOvertime = useMemo(() => overtimeRows.reduce((s, o) => s + (o.heures || 0.0), 0), [overtimeRows]);
    const totalTheo  = useMemo(() => wDays.reduce((s, d) => s + (d.isOff ? 0 : d.normalHours), 0), [wDays]);

    const goPrev = () => setCurrentDate(prev => new Date(prev.setDate(prev.getDate() - 7)));
    const goNext = () => setCurrentDate(prev => new Date(prev.setDate(prev.getDate() + 7)));

    const periodLabel = useMemo(() => {
        const s = new Date(periodStart).toLocaleDateString("fr-FR", { day: "2-digit", month: "short" });
        const e = new Date(periodEnd).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
        return `${s} — ${e}`;
    }, [periodStart, periodEnd]);

    const statusInfo = useMemo(() => {
        const s = statut ? statut.toUpperCase() : "NEW";
        switch(s) {
            case "APPROVED":
                return { label: "Approuvée", cls: "approved", icon: <CheckCircle size={12}/> };
            case "REJECTED":
                return { label: "Rejetee", cls: "rejected", icon: <AlertCircle size={12}/> };
            case "PENDING_MANAGER":
                return { label: "En attente du manager", cls: "pending-manager", icon: <Clock size={12}/> };
            case "PENDING_ADMIN":
                return { label: "En attente RH", cls: "pending-admin", icon: <Clock size={12}/> };
            case "SUBMITTED":
                return { label: "Soumise", cls: "submitted", icon: <CheckCircle size={12}/> };
            case "DRAFT":
                return { label: "Brouillon", cls: "draft", icon: <Clock size={12}/> };
            default:
                return { label: "Non soumise", cls: "new", icon: <Clock size={12}/> };
        }
    }, [statut]);

    return (
        <div className="ts-root mesOrdresTimeEntry">
            <div className="ts-topbar">
                <div className="ts-topbar__left">
                    <div><span className="ts-title">Mes feuilles de temps</span><span className="ts-subtitle">Saisissez vos heures de travail et vos projets.</span></div>
                </div>
            </div>

            <div className="ts-subtabs">
                <button className={`ts-subtab-btn ${activeTab === "normal" ? "is-active" : ""}`} onClick={() => setActiveTab("normal")}>
                    <Calendar size={14}/> Saisie de la feuille de temps
                </button>
            </div>

            <div className="ts-body">
                {activeTab === "normal" && (
                    <div className="ts-nav">
                        <div className="ts-nav__center">
                            <button className="ts-arr" onClick={goPrev}><ChevronLeft size={16}/></button>
                            <div className="ts-period">{periodLabel}</div>
                            <button className="ts-arr" onClick={goNext}><ChevronRight size={16}/></button>
                        </div>
                        <div className={`ts-chip ts-chip--${statusInfo.cls}`} style={getStatusStyle(statusInfo.cls)}>
                            {statusInfo.icon} {statusInfo.label}
                        </div>
                    </div>
                )}

                {activeTab === "normal" && !canEdit && (
                    <div className="ts-banner ts-banner--info">
                        <Lock size={14}/>
                        <span>Feuille en lecture seule. Vous ne pouvez pas la modifier car elle a été soumise ou approuvée.</span>
                    </div>
                )}

                {/* ── ONGLET 1 : HEURES NORMALES ── */}
                {activeTab === "normal" && (
                    <>
                        <div className="ts-rows">
                            {wDays.map((day, i) => {
                                const locked = !canEdit || day.isFutureDay || day.isOff;
                                return (
                                    <div key={i} className={`ts-row ${day.isFutureDay ? "--future-day" : ""} ${day.isToday ? "--today" : ""}`}>
                                        <div className="ts-row__date">
                                            <span className="ts-row__daynum">{day.dateObj.getDate()}</span>
                                            <div className="ts-row__daylabels">
                                                <span className="ts-row__dayname">{day.full}</span>
                                                <span className="ts-row__month">{day.isOff ? "Non ouvré" : `${day.normalHours}h max`}</span>
                                            </div>
                                        </div>

                                        <div className="ts-row__middle">
                                            {day.isFutureDay ? (
                                                <span className="ts-future-label"><Lock size={12}/> Jour non arrivé</span>
                                            ) : (
                                                <div className="ts-inputs-group">
                                                    <div className="ts-input-hour">
                                                        <label>Heures :</label>
                                                        <input
                                                            className="ts-row__input"
                                                            type="number"
                                                            min="0"
                                                            max={day.normalHours || 24}
                                                            value={day.hours || ""}
                                                            placeholder="0"
                                                            disabled={locked}
                                                            onChange={e => handleHourChange(day.dateKey, day.dateObj, e.target.value)}
                                                        />
                                                    </div>

                                                    <div className="ts-input-project">
                                                        <label>Projet :</label>
                                                        <select
                                                            className="ts-project-select"
                                                            value={day.selectedProj}
                                                            disabled={locked}
                                                            onChange={e => handleProjectChange(day.dateKey, day.dateObj, e.target.value)}
                                                        >
                                                            <option value="">-- Aucun --</option>
                                                            {myProjets.map(p => (
                                                                <option key={p.id} value={p.id}>{p.code} - {p.nom}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="ts-summary-footer">
                            <div className="ts-summary-info">
                                <span>Heures Normales : <strong>{totalSaisi}h</strong> / {totalTheo}h</span>
                            </div>
                            {canEdit && (
                                <button className="ts-submit-btn" disabled={totalSaisi === 0 || submitting} onClick={handleSaisieSubmit}>
                                    {submitting ? <><RefreshCw size={14} className="ts-spin"/> Transmission...</> : "Soumettre ma feuille de temps"}
                                </button>
                            )}
                        </div>
                    </>
                )}

                {/* ── ONGLET 2 : HEURES SUPPLÉMENTAIRES ── */}
                {activeTab === "overtime" && (
                    <div className="ts-overtime-container">
                        <form className="ts-overtime-form" onSubmit={handleAddOvertime}>
                            <h3>Déclarer vos heures supplémentaires</h3>
                            {isOvertimeClosed && (
                                <div className="ts-banner ts-banner--info" style={{ marginBottom: 12 }}>
                                    <Lock size={14}/>
                                    <span>Les heures supplementaires sont fermees pour la date sélectionnée car la feuille de temps est soumise ou approuvée.</span>
                                </div>
                            )}
                            <div className="ts-form-grid">
                                <div className="ts-form-field">
                                    <label>Date :</label>
                                    <input type="date" value={ovDate} onChange={e => setOvDate(e.target.value)} required />
                                </div>
                                <div className="ts-form-field">
                                    <label>Début :</label>
                                    <input type="time" value={ovStart} onChange={e => setOvStart(e.target.value)} required disabled={isOvertimeClosed} />
                                </div>
                                <div className="ts-form-field">
                                    <label>Fin :</label>
                                    <input type="time" value={ovEnd} onChange={e => setOvEnd(e.target.value)} required disabled={isOvertimeClosed} />
                                </div>
                                <div className="ts-form-field">
                                    <label>Projet :</label>
                                    <select value={ovProj} onChange={e => setOvProj(e.target.value)} disabled={isOvertimeClosed}>
                                        <option value="">-- Aucun --</option>
                                        {myProjets.map(p => (
                                            <option key={p.id} value={p.id}>{p.code} - {p.nom}</option>
                                        ))}
                                    </select>
                                </div>
                                <div className="ts-form-field --full">
                                    <label>Motif / Commentaire :</label>
                                    <input type="text" placeholder="Ex: Réunion client tardive, astreinte..." value={ovComment} onChange={e => setOvComment(e.target.value)} disabled={isOvertimeClosed} />
                                </div>
                            </div>
                            <button type="submit" className="ts-add-ov-btn" disabled={overtimeSubmitting}>
                                {overtimeSubmitting ? <><RefreshCw size={14} className="ts-spin"/> Ajout...</> : <><Plus size={14}/> Ajouter à la feuille</>}
                            </button>
                            {overtimeFeedback && <div className="ts-banner ts-banner--info" style={{ marginTop: 12 }}>{overtimeFeedback}</div>}
                        </form>
                        <div className="ts-overtime-list">
                            <h3>Heures supplémentaires enregistrées ({totalOvertime}h)</h3>
                            {overtimeRows.length === 0 ? (
                                <p className="ts-no-data">Aucune heure supplémentaire déclarée pour la date sélectionnée.</p>
                            ) : (
                                <table className="ts-table-overtime">
                                    <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Horaires</th>
                                        <th>Durée</th>
                                        <th>Projet</th>
                                        <th>Motif</th>
                                        <th>Statut</th>
                                        <th>Actions</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {overtimeRows.map(o => {
                                        const canEditOT = o.isDemo
                                            ? (o.statut === "DRAFT" || o.statut === "REJECTED" || !o.statut)
                                            : canEditOvertime && (o.statut === "DRAFT" || o.statut === "REJECTED" || !o.statut);
                                        return (
                                            <tr key={o.id}>
                                                <td>{new Date(o.workDate).toLocaleDateString("fr-FR")}</td>
                                                <td>{o.startTime?.substring(0, 5)} - {o.endTime?.substring(0, 5)}</td>
                                                <td>{o.heures}h</td>
                                                <td>{o.projetNom || "—"}</td>
                                                <td>
                                                    <div>{o.commentaire || "—"}</div>
                                                    {o.statut === "REJECTED" && (o.managerComment || o.adminComment) && (
                                                        <div style={{
                                                            fontSize: "11px",
                                                            color: "#ef4444",
                                                            marginTop: "4px"
                                                        }}>
                                                            Refus: {o.managerComment || o.adminComment}
                                                        </div>
                                                    )}
                                                </td>
                                                <td>
                                                        <span
                                                            className={`ts-history-badge --${(o.statut || "DRAFT").toLowerCase()}`}>
                                                            {o.statut || "DRAFT"}
                                                        </span>
                                                </td>
                                                <td>
                                                    <div style={{display: "flex", gap: "6px", alignItems: "center"}}>
                                                        {canEditOT && (
                                                            <button
                                                                type="button"
                                                                className="ts-submit-btn-small"
                                                                style={{
                                                                    padding: "4px 8px",
                                                                    fontSize: "11px",
                                                                    background: "var(--navy)",
                                                                    color: "#fff",
                                                                    border: "none",
                                                                    borderRadius: "4px",
                                                                    cursor: "pointer"
                                                                }}
                                                                onClick={() => handleSubmitOvertime(o.id, o.isDemo)}
                                                            >
                                                                Soumettre
                                                            </button>
                                                        )}
                                                        {canEditOT && (
                                                            <button type="button" className="ts-btn-delete"
                                                                    onClick={() => handleDeleteOvertime(o.id, o.isDemo)}>
                                                                <Trash2 size={13}/></button>
                                                        )}
                                                        {!canEditOT && (
                                                            <span style={{
                                                                color: "#9ca3af",
                                                                display: "flex",
                                                                alignItems: "center"
                                                            }}><Lock size={12}/></span>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                )}

                {/* ── ONGLET 3 : HISTORIQUE ── */}
                {activeTab === "history" && (
                    <div className="ts-history-container">
                        <h3>Mes feuilles de temps précédentes</h3>
                        {timesheets.length === 0 ? (
                            <p className="ts-no-data">Aucune feuille de temps enregistrée.</p>
                        ) : (
                            <table className="ts-table-history">
                                <thead>
                                <tr>
                                    <th>Période</th>
                                    <th>Statut</th>
                                    <th>Heures Normales</th>
                                    <th>Heures Supp.</th>
                                    <th>Total</th>
                                    <th>Action</th>
                                </tr>
                                </thead>
                                <tbody>
                                {timesheets.map(t => {
                                    const display = getStatusDisplay(t.statut);
                                    return (
                                        <tr key={t.id}>
                                            <td>Du {new Date(t.periodStart).toLocaleDateString("fr-FR")} au {new Date(t.periodEnd).toLocaleDateString("fr-FR")}</td>
                                            <td>
                                                    <span className={`ts-history-badge --${display.cls}`} style={getStatusStyle(display.cls)}>
                                                        {display.label}
                                                    </span>
                                            </td>
                                            <td>{t.totalHeures - t.heuresSupp}h</td>
                                            <td>{t.heuresSupp}h</td>
                                            <td><strong>{t.totalHeures}h</strong></td>
                                            <td>
                                                <button className="ts-btn-view" onClick={() => handleViewDetails(t)}>
                                                    <Eye size={13}/> Consulter
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </div>

            {/* ── POPUP DÉTAILS DE L'HISTORIQUE ── */}
            {viewingDetails && selectedHistorySheet && (
                <div className="ts-modal-overlay">
                    <div className="ts-modal">
                        <div className="ts-modal-header">
                            <h3>Feuille du {new Date(selectedHistorySheet.periodStart).toLocaleDateString("fr-FR")} au {new Date(selectedHistorySheet.periodEnd).toLocaleDateString("fr-FR")}</h3>
                            <button className="ts-modal-close" onClick={() => setViewingDetails(false)}><X size={18}/></button>
                        </div>
                        <div className="ts-modal-body">
                            <h4>1. Heures Normales Saisies</h4>
                            {historyDays.length === 0 ? (
                                <p className="ts-no-data">Aucune heure normale.</p>
                            ) : (
                                <table className="ts-modal-table">
                                    <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Heures</th>
                                        <th>Projet</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {historyDays.filter(d => d.heures > 0).map(d => (
                                        <tr key={d.id}>
                                            <td>{new Date(d.workDate).toLocaleDateString("fr-FR")}</td>
                                            <td>{d.heures}h</td>
                                            <td>{d.projetNom || "—"}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            )}

                            <h4>2. Heures Supplémentaires Déclarées</h4>
                            {historyOvertimes.length === 0 ? (
                                <p className="ts-no-data">Aucune heure supplémentaire.</p>
                            ) : (
                                <table className="ts-modal-table">
                                    <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Plage</th>
                                        <th>Durée</th>
                                        <th>Projet</th>
                                        <th>Commentaire</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {historyOvertimes.map(o => (
                                        <tr key={o.id}>
                                            <td>{new Date(o.workDate).toLocaleDateString("fr-FR")}</td>
                                            <td>{o.startTime?.substring(0,5)} - {o.endTime?.substring(0,5)}</td>
                                            <td>{o.heures}h</td>
                                            <td>{o.projetNom || "—"}</td>
                                            <td>{o.commentaire || "—"}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FeuillesTemps;
